import axios from 'axios';

import type {
  GeneralErrorResponse,
  GeneralResponse,
  GlobalErrorResponse,
  ValidationProblemDetails,
} from './backend-envelopes';

/**
 * How a request failed, independent of HTTP status.
 *
 * `network` and `timeout` have no status code but need distinct UI treatment
 * ("check your connection" vs "the server is slow"), so they are modelled
 * explicitly rather than collapsed into status 0.
 */
export type ApiErrorKind = 'http' | 'network' | 'timeout' | 'cancelled' | 'unknown';

/**
 * The single error type the application deals with.
 *
 * Components never inspect an `AxiosError`. Everything that leaves the API
 * layer is an `ApiError` with a message already chosen and field errors already
 * flattened (§15).
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  /** 0 for network/timeout failures, which never reached the server. */
  readonly status: number;
  /** Backend code where one exists — "Valdata1", "NotFound", "422", … */
  readonly code: string | null;
  /** Flattened per-field messages, keyed by field name, for form binding. */
  readonly fieldErrors: Record<string, string> | null;
  /** The raw payload, for logging and the dev-only error display. */
  readonly details: unknown;

  constructor(init: {
    message: string;
    kind: ApiErrorKind;
    status: number;
    code?: string | null;
    fieldErrors?: Record<string, string> | null;
    details?: unknown;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code ?? null;
    this.fieldErrors = init.fieldErrors ?? null;
    this.details = init.details;
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isNotFound() {
    return this.status === 404;
  }

  /** 4xx that the user could plausibly fix by changing their input. */
  get isValidation() {
    return this.status === 400 || this.status === 409 || this.status === 422;
  }

  /** Worth offering a "Try again" button for. */
  get isRetryable() {
    return this.kind === 'network' || this.kind === 'timeout' || this.status >= 500;
  }
}

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Messages safe to show the user verbatim.
 *
 * A 500 from the global handler can carry an internal exception message, so
 * server-side failures get a generic message instead and the real text is kept
 * on `details` for logging only (§15).
 */
function isSafeToDisplay(status: number): boolean {
  return status > 0 && status < 500;
}

/**
 * Some backend field errors are bilingual, pipe-separated: "English || عربي".
 * The admin UI is English, so take the left side.
 */
function pickMessage(raw: string): string {
  if (!raw.includes('||')) return raw.trim();
  const [english] = raw.split('||');
  return (english ?? raw).trim();
}

/**
 * Flatten a field-error bag to one message per field.
 *
 * The two envelopes disagree about the value type and there is no reliable way
 * to tell which one you are holding from the status code alone:
 *
 *   GeneralResponse.Errors        → Record<string, string>
 *   ValidationProblemDetails.errors → Record<string, string[]>
 *
 * So each value is narrowed individually. Getting this wrong is not a type
 * error at runtime — indexing `[0]` into a string silently yields its first
 * character, turning "Name is required" into "N". That bug shipped once and is
 * now covered by a test.
 */
function flattenFieldErrors(errors: unknown): Record<string, string> | null {
  if (typeof errors !== 'object' || errors === null) return null;

  const flattened: Record<string, string> = {};

  for (const [field, value] of Object.entries(errors as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      const first = value[0];
      if (typeof first === 'string' && first) flattened[field] = pickMessage(first);
    } else if (typeof value === 'string' && value) {
      flattened[field] = pickMessage(value);
    }
  }

  return Object.keys(flattened).length > 0 ? flattened : null;
}

/**
 * Normalize anything thrown by axios into an `ApiError`.
 *
 * Message precedence, chosen to surface the most specific text available:
 *   field error → GeneralResponse.messageEn → GlobalError.MessageEn
 *   → GeneralErrorResponse.message → ProblemDetails.title → generic fallback
 */
export function normalizeApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isCancel(error)) {
    return new ApiError({
      message: 'Request cancelled.',
      kind: 'cancelled',
      status: 0,
      details: error,
    });
  }

  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new ApiError({
        message: 'The server took too long to respond. Please try again.',
        kind: 'timeout',
        status: 0,
        code: error.code,
        details: error.message,
      });
    }

    if (!error.response) {
      return new ApiError({
        message: 'Could not reach the server. Check your connection and try again.',
        kind: 'network',
        status: 0,
        code: error.code ?? null,
        details: error.message,
      });
    }

    const { status, data } = error.response;
    const payload = data as Partial<GeneralResponse<unknown>> &
      Partial<GeneralErrorResponse> &
      Partial<ValidationProblemDetails> &
      Partial<GlobalErrorResponse>;

    const fieldErrors = flattenFieldErrors(payload?.errors);

    const backendMessage =
      payload?.messageEn ?? payload?.MessageEn ?? payload?.message ?? payload?.title ?? null;

    const firstFieldError = fieldErrors ? Object.values(fieldErrors)[0] : undefined;

    const message =
      firstFieldError ??
      (isSafeToDisplay(status) && backendMessage ? backendMessage : FALLBACK_MESSAGE);

    return new ApiError({
      message,
      kind: 'http',
      status,
      code: payload?.code ?? payload?.Code ?? payload?.errorCode ?? String(status),
      fieldErrors,
      details: data,
    });
  }

  return new ApiError({
    message: error instanceof Error ? error.message : FALLBACK_MESSAGE,
    kind: 'unknown',
    status: 0,
    details: error,
  });
}

/** Convenience for `catch` blocks and error boundaries. */
export function getErrorMessage(error: unknown, fallback = FALLBACK_MESSAGE): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
